import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import Footer from '../components/layout/Footer';

const Signup = () => {
    const { t } = useTranslation('auth');
    const navigate = useNavigate();
    const location = useLocation();
    const { register, error: authError, clearError } = useAuth();

    const from = location.state?.from || '/';
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        password: '',
        confirmPassword: '',
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [validationErrors, setValidationErrors] = useState({});
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const validateForm = () => {
        const errors = {};
        if (formData.password.length < 8) {
            errors.password = t('signup.validation.passwordLength');
        }
        if (formData.password !== formData.confirmPassword) {
            errors.confirmPassword = t('signup.validation.passwordMatch');
        }
        if (!formData.email.includes('@')) {
            errors.email = t('signup.validation.emailInvalid');
        }
        if (formData.firstName.trim().length < 2) {
            errors.firstName = t('signup.validation.firstNameLength');
        }
        if (formData.lastName.trim().length < 2) {
            errors.lastName = t('signup.validation.lastNameLength');
        }
        if (formData.phoneNumber.trim() && !/^\+?1?\d{10}$/.test(formData.phoneNumber.replace(/[^0-9+]/g, ''))) {
            errors.phoneNumber = t('signup.validation.phoneInvalid');
        }
        return errors;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));
        if (validationErrors[name]) {
            setValidationErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setLoading(true);
        setError('');
        setValidationErrors({});

        const errors = validateForm();
        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            setLoading(false);
            return;
        }

        try {
            const { confirmPassword, ...formFields } = formData;
            const signupData = {
                name: `${formFields.firstName} ${formFields.lastName}`,
                email: formFields.email,
                password: formFields.password,
                role: 'student',
                status: 'active',
                first_name: formFields.firstName,
                last_name: formFields.lastName,
                phone_number: formFields.phoneNumber.trim() || null,
                email_notifications: true,
            };

            const result = await register(signupData);

            if (result.success) {
                clearError();
                if (result.requiresVerification) {
                    const pendingEmail = result.email || formFields.email;
                    sessionStorage.setItem('pendingVerificationEmail', pendingEmail);
                    navigate(`/verify-email-pending?email=${encodeURIComponent(pendingEmail)}`, {
                        replace: true,
                        state: { email: pendingEmail }
                    });
                } else {
                    navigate(from, { replace: true });
                }
            }
        } catch (err) {
            setError(err.message || t('signup.registerFailed'));
        } finally {
            setLoading(false);
        }
    };

    const getInputClassName = (fieldName) => {
        const baseClasses = "w-full px-3 sm:px-4 py-2 sm:py-3 border focus:outline-none transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed text-sm sm:text-base";
        const errorClasses = validationErrors[fieldName] ? "border-red-300" : "border-gray-200 focus:border-black";
        return `${baseClasses} ${errorClasses}`;
    };

    return (
        <div className="bg-white min-h-screen font-montserrat">
            <section className="relative w-full h-[200px] sm:h-[250px] md:h-[300px] flex items-center justify-center text-white text-center overflow-hidden mb-0 px-4 sm:px-6 lg:px-8" style={{ margin: '10px auto' }}>
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-blue-800 z-0" />
                <div className="relative z-20 flex flex-col items-center justify-center w-full h-full">
                    <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-normal mb-3 sm:mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>{t('signup.heroTitle')}</h1>
                    <p className="text-base sm:text-lg md:text-xl max-w-2xl mx-auto opacity-90 px-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>{t('signup.heroSubtitle')}</p>
                </div>
            </section>

            <div className="w-full border-t border-gray-200 my-0" />

            <section className="py-8 sm:py-12 lg:py-16 max-w-md mx-auto px-4 sm:px-6 lg:px-8">
                {(error || authError) && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded">
                        {error || authError}
                    </div>
                )}

                <form
                    className="space-y-4 sm:space-y-6"
                    onSubmit={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSubmit(e);
                        return false;
                    }}
                    noValidate
                >
                    <div className="grid grid-cols-1 gap-4 sm:gap-6">
                        <div>
                            <label htmlFor="firstName" className="block text-sm uppercase tracking-widest mb-2" style={{ color: '#979797', fontSize: '13px', fontFamily: 'Montserrat, sans-serif' }}>
                                {t('signup.firstName')}
                            </label>
                            <input
                                id="firstName"
                                name="firstName"
                                type="text"
                                value={formData.firstName}
                                onChange={handleChange}
                                disabled={loading}
                                className={getInputClassName('firstName')}
                                style={{ fontFamily: 'Montserrat, sans-serif' }}
                            />
                            {validationErrors.firstName && (
                                <p className="mt-1 text-sm text-red-600">{validationErrors.firstName}</p>
                            )}
                        </div>

                        <div>
                            <label htmlFor="lastName" className="block text-sm uppercase tracking-widest mb-2" style={{ color: '#979797', fontSize: '13px', fontFamily: 'Montserrat, sans-serif' }}>
                                {t('signup.lastName')}
                            </label>
                            <input
                                id="lastName"
                                name="lastName"
                                type="text"
                                value={formData.lastName}
                                onChange={handleChange}
                                disabled={loading}
                                className={getInputClassName('lastName')}
                                style={{ fontFamily: 'Montserrat, sans-serif' }}
                            />
                            {validationErrors.lastName && (
                                <p className="mt-1 text-sm text-red-600">{validationErrors.lastName}</p>
                            )}
                        </div>
                    </div>

                    <div>
                        <label htmlFor="email" className="block text-sm uppercase tracking-widest mb-2" style={{ color: '#979797', fontSize: '13px', fontFamily: 'Montserrat, sans-serif' }}>
                            {t('signup.email')}
                        </label>
                        <input
                            id="email"
                            name="email"
                            type="email"
                            autoComplete="email"
                            value={formData.email}
                            onChange={handleChange}
                            disabled={loading}
                            className={getInputClassName('email')}
                            style={{ fontFamily: 'Montserrat, sans-serif' }}
                        />
                        {validationErrors.email && (
                            <p className="mt-1 text-sm text-red-600">{validationErrors.email}</p>
                        )}
                    </div>

                    <div>
                        <label htmlFor="phoneNumber" className="block text-sm uppercase tracking-widest mb-2" style={{ color: '#979797', fontSize: '13px', fontFamily: 'Montserrat, sans-serif' }}>
                            {t('signup.phoneOptional')}
                        </label>
                        <input
                            id="phoneNumber"
                            name="phoneNumber"
                            type="tel"
                            autoComplete="tel"
                            value={formData.phoneNumber}
                            onChange={handleChange}
                            disabled={loading}
                            className={getInputClassName('phoneNumber')}
                            placeholder={t('signup.phonePlaceholder')}
                            style={{ fontFamily: 'Montserrat, sans-serif' }}
                        />
                        {validationErrors.phoneNumber && (
                            <p className="mt-1 text-sm text-red-600">{validationErrors.phoneNumber}</p>
                        )}
                    </div>

                    <div>
                        <label htmlFor="password" className="block text-sm uppercase tracking-widest mb-2" style={{ color: '#979797', fontSize: '13px', fontFamily: 'Montserrat, sans-serif' }}>
                            {t('signup.password')}
                        </label>
                        <div className="relative">
                            <input
                                id="password"
                                name="password"
                                type={showPassword ? "text" : "password"}
                                value={formData.password}
                                onChange={handleChange}
                                disabled={loading}
                                className={getInputClassName('password') + " pr-12"}
                                style={{ fontFamily: 'Montserrat, sans-serif' }}
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                disabled={loading}
                            >
                                {showPassword ? (
                                    <svg className="h-5 w-5 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                                    </svg>
                                ) : (
                                    <svg className="h-5 w-5 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                    </svg>
                                )}
                            </button>
                        </div>
                        {validationErrors.password && (
                            <p className="mt-1 text-sm text-red-600">{validationErrors.password}</p>
                        )}
                    </div>

                    <div>
                        <label htmlFor="confirmPassword" className="block text-sm uppercase tracking-widest mb-2" style={{ color: '#979797', fontSize: '13px', fontFamily: 'Montserrat, sans-serif' }}>
                            {t('signup.confirmPassword')}
                        </label>
                        <div className="relative">
                            <input
                                id="confirmPassword"
                                name="confirmPassword"
                                type={showConfirmPassword ? "text" : "password"}
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                disabled={loading}
                                className={getInputClassName('confirmPassword') + " pr-12"}
                                style={{ fontFamily: 'Montserrat, sans-serif' }}
                            />
                            <button
                                type="button"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                disabled={loading}
                            >
                                {showConfirmPassword ? (
                                    <svg className="h-5 w-5 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                                    </svg>
                                ) : (
                                    <svg className="h-5 w-5 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                    </svg>
                                )}
                            </button>
                        </div>
                        {validationErrors.confirmPassword && (
                            <p className="mt-1 text-sm text-red-600">{validationErrors.confirmPassword}</p>
                        )}
                    </div>

                    <div>
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-black text-white px-6 sm:px-8 py-3 sm:py-4 font-normal border-0 hover:bg-gray-900 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed relative text-sm sm:text-base"
                            style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 400 }}
                        >
                            {loading ? (
                                <>
                                    <span className="opacity-0">{t('signup.createAccount')}</span>
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    </div>
                                </>
                            ) : (
                                t('signup.createAccount')
                            )}
                        </button>
                    </div>
                </form>

                <div className="mt-6 sm:mt-8 text-center">
                    <p className="text-gray-700 text-sm sm:text-base" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        {t('signup.hasAccount')}{' '}
                        <Link to="/login" className="text-black hover:underline" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                            {t('signup.login')}
                        </Link>
                    </p>
                </div>
            </section>

            <div className="w-full border-t border-gray-200 my-0" />

            <Footer />
        </div>
    );
};

export default Signup;
